import React, { useRef } from 'react';
import { gsap, useGSAP, prefersReducedMotion } from '../../../lib/gsap';

const SRC = `${process.env.PUBLIC_URL}/images/trump-wave.png`;

/**
 * Duotone cut-out portrait anchored to the bottom-right of the header.
 * Grayscale photo + signal-red multiply layer, faded at the edges with a mask
 * so it sits *in* the dark page rather than on top of it.
 */
const TrumpPortrait = ({ className = '' }: { className?: string }) => {
   const root = useRef<HTMLDivElement>(null);

   useGSAP(
      () => {
         if (prefersReducedMotion()) return;
         gsap.from(root.current, { autoAlpha: 0, yPercent: 12, duration: 2.2, delay: 0.2, ease: 'expo.out' });

         // Slow pointer drift (opposite to the headline collage, for depth)
         const x = gsap.quickTo(root.current, 'x', { duration: 1.6, ease: 'power3.out' });
         const move = (e: PointerEvent) => x((e.clientX / window.innerWidth - 0.5) * 24);
         window.addEventListener('pointermove', move, { passive: true });

         // Sink and fade as the header scrolls away
         gsap.to(root.current, {
            yPercent: 18,
            autoAlpha: 0.15,
            ease: 'none',
            scrollTrigger: { trigger: root.current, start: 'top top', end: 'bottom top', scrub: true },
         });
         return () => window.removeEventListener('pointermove', move);
      },
      { scope: root }
   );

   const mask = 'linear-gradient(to top, transparent 0%, black 28%), linear-gradient(to right, transparent 0%, black 22%)';

   return (
      <div
         ref={root}
         aria-hidden="true"
         className={`pointer-events-none absolute bottom-0 aspect-[673/768] select-none ${className}`}
         style={{ maskImage: mask, WebkitMaskImage: mask, maskComposite: 'intersect', WebkitMaskComposite: 'source-in' }}
      >
         <div className="relative h-full">
            <img src={SRC} alt="" className="block h-full w-full object-contain grayscale contrast-125 brightness-90" draggable={false} />
            {/* Red duotone: tint only where the cut-out has pixels */}
            <div
               className="absolute inset-0 bg-signal mix-blend-multiply"
               style={{
                  maskImage: `url(${SRC})`,
                  WebkitMaskImage: `url(${SRC})`,
                  maskSize: 'contain',
                  WebkitMaskSize: 'contain',
                  maskRepeat: 'no-repeat',
                  WebkitMaskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  WebkitMaskPosition: 'center',
               }}
            />
         </div>
      </div>
   );
};

export default TrumpPortrait;
